import { useEffect, useMemo, useState } from 'react';
import { iptv, type IptvChannel, type League, type WidgetSource } from '../api';
import { loadLeagues } from '../sport';
import { ChannelLogo, ChannelName } from './LiveBits';

type LiveSource = Extract<WidgetSource, { kind: 'live' }>;

/** Live sport: welke sporten/competities, alleen NL-zenders, hoe ver vooruit. */
export function SportFilter({ value, onChange }: { value: LiveSource; onChange: (p: Partial<LiveSource>) => void }) {
  const [leagues, setLeagues] = useState<League[] | null>(null);
  useEffect(() => {
    loadLeagues().then(setLeagues).catch(() => setLeagues([]));
  }, []);
  const sports = useMemo(() => [...new Map((leagues ?? []).map((l) => [l.sport, l.sportName])).entries()], [leagues]);
  const chosenSports = value.sports ?? [];
  const chosenLeagues = value.leagues ?? [];
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const visibleLeagues = (leagues ?? []).filter((l) => !chosenSports.length || chosenSports.includes(l.sport));

  return (
    <>
      <div className="ed-field">
        <span>Sporten {chosenSports.length ? `(${chosenSports.length})` : '(alles)'}</span>
        <div className="chip-wrap">
          {sports.map(([id, name]) => (
            <button key={id} className="chip" aria-pressed={chosenSports.includes(id)} onClick={() => onChange({ sports: toggle(chosenSports, id), leagues: undefined })}>{name}</button>
          ))}
        </div>
      </div>
      <div className="ed-field">
        <span>Competities {chosenLeagues.length ? `(${chosenLeagues.length})` : '(alles)'}</span>
        <div className="chip-wrap">
          {visibleLeagues.map((l) => (
            <button key={l.id} className="chip league-chip" aria-pressed={chosenLeagues.includes(l.id)} onClick={() => onChange({ leagues: toggle(chosenLeagues, l.id) })}>
              {(l.badge ?? l.logo) && <img src={(l.badge ?? l.logo)!} alt="" referrerPolicy="no-referrer" />}
              {l.name}
            </button>
          ))}
        </div>
      </div>
      <div className="ed-field">
        <span>Zenders</span>
        <div className="chip-wrap">
          <button className="chip" aria-pressed={!value.nlOnly} onClick={() => onChange({ nlOnly: undefined })}>Alle zenders</button>
          <button className="chip" aria-pressed={!!value.nlOnly} onClick={() => onChange({ nlOnly: true })}>Alleen Nederlandse zenders</button>
        </div>
      </div>
      <div className="ed-field">
        <span>Hoe ver vooruit</span>
        <div className="chip-wrap">
          {[6, 12, 24, 48].map((h) => (
            <button key={h} className="chip" aria-pressed={(value.hours ?? 12) === h} onClick={() => onChange({ hours: h })}>{h === 48 ? '2 dagen' : `${h} uur`}</button>
          ))}
        </div>
      </div>
      <span className="subtle hint">Niets gekozen = alle sporten en competities. Live eerst, jouw clubs eerst, herhalingen weggelaten.</span>
    </>
  );
}

/** Nu op tv: automatisch (favorieten), vaste zenders in eigen volgorde, of hele categorieën. */
export function NowTvPicker({ channels, categories, onChange }: { channels?: string[]; categories?: string[]; onChange: (channels: string[], categories: string[]) => void }) {
  const [data, setData] = useState<{ channels: IptvChannel[]; categories: { id: string; accountId: string; name: string }[] } | null>(null);
  const [mode, setMode] = useState<'auto' | 'channels' | 'categories'>(channels?.length ? 'channels' : categories?.length ? 'categories' : 'auto');
  const [q, setQ] = useState('');
  useEffect(() => {
    iptv.channels().then((r) => setData({ channels: r.channels, categories: r.categories })).catch(() => setData({ channels: [], categories: [] }));
  }, []);

  const chosen = channels ?? [];
  const cats = categories ?? [];
  const byKey = useMemo(() => new Map((data?.channels ?? []).map((c) => [`${c.accountId}:${c.streamId}`, c])), [data]);
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const results = q.trim().length >= 2 ? (data?.channels ?? []).filter((c) => !c.eventTitle && norm(c.name).includes(norm(q.trim())) && !chosen.includes(`${c.accountId}:${c.streamId}`)).slice(0, 12) : [];
  const catResults = (data?.categories ?? []).filter((c) => !q.trim() || norm(c.name).includes(norm(q.trim()))).slice(0, 60);

  const move = (i: number, d: -1 | 1) => {
    const n = [...chosen];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n, []);
  };

  return (
    <>
      <div className="ed-field">
        <span>Welke zenders</span>
        <div className="chip-wrap">
          <button className="chip" aria-pressed={mode === 'auto'} onClick={() => { setMode('auto'); onChange([], []); }}>Automatisch</button>
          <button className="chip" aria-pressed={mode === 'channels'} onClick={() => { setMode('channels'); setQ(''); }}>Zelf kiezen</button>
          <button className="chip" aria-pressed={mode === 'categories'} onClick={() => { setMode('categories'); setQ(''); }}>Hele categorieën</button>
        </div>
      </div>

      {mode === 'auto' && <span className="subtle hint">Je favoriete zenders (ster in Live TV → Gids), anders Nederlandse zenders.</span>}

      {mode !== 'auto' && (
        <label className="ed-field">{mode === 'channels' ? 'Zender zoeken' : 'Categorie zoeken'}
          <input className="text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={mode === 'channels' ? 'Bijv. NPO 1, RTL 4, Ziggo Sport…' : 'Bijv. NL, Sport, Kids…'} />
        </label>
      )}

      {mode === 'channels' && (
        <>
          {results.length > 0 && (
            <div className="pick-list" role="listbox" aria-label="Zoekresultaten">
              {results.map((c) => (
                <button key={`${c.accountId}:${c.streamId}`} className="pick-row" onClick={() => { onChange([...chosen, `${c.accountId}:${c.streamId}`], []); setQ(''); }}>
                  <ChannelLogo src={c.logo} name={c.name} size={24} />
                  <ChannelName name={c.name} />
                  <span className="subtle">{c.categoryName}</span>
                  <span className="pick-add">+ Toevoegen</span>
                </button>
              ))}
            </div>
          )}
          <div className="ed-field">
            <span>Gekozen zenders ({chosen.length})</span>
            {!chosen.length && <span className="subtle hint">Zoek hierboven en voeg zenders toe; de volgorde hier is de volgorde in de rij.</span>}
            <ol className="nav-edit">
              {chosen.map((k, i) => {
                const c = byKey.get(k);
                return (
                  <li key={k}>
                    {c ? <><ChannelLogo src={c.logo} name={c.name} size={24} /><span className="nav-edit-name"><ChannelName name={c.name} /></span></> : <span className="nav-edit-name subtle">Zender niet (meer) beschikbaar</span>}
                    <button className="btn btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Omhoog">↑</button>
                    <button className="btn btn-sm" onClick={() => move(i, 1)} disabled={i === chosen.length - 1} aria-label="Omlaag">↓</button>
                    <button className="btn btn-sm" onClick={() => onChange(chosen.filter((x) => x !== k), [])} aria-label="Verwijderen">✕</button>
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}

      {mode === 'categories' && (
        <div className="ed-field">
          <span>Categorieën ({cats.length})</span>
          <div className="chip-wrap">
            {catResults.map((c) => (
              <button key={c.id} className="chip" aria-pressed={cats.includes(c.id)} onClick={() => onChange([], cats.includes(c.id) ? cats.filter((x) => x !== c.id) : [...cats, c.id])}>{c.name}</button>
            ))}
          </div>
          <span className="subtle hint">Alle zenders uit deze categorieën, in de volgorde van je provider.</span>
        </div>
      )}
    </>
  );
}
